import { graphqlPost } from "api/graphqlClient";
import StatusBadge from "components/StatusBadge/StatusBadge";
import { isEqual } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { DashboardLiveWidgetShell } from "../DashboardLiveWidgetShell";
import { describeWidgetFetchError } from "../dashboardWidgetError";
import { StatusTileWidgetConfig, WidgetComponentProps } from "../types";
import { useDashboardLiveRefresh } from "../useDashboardLiveRefresh";
import { formatStatusTileDetail, resolveStatusTile, TileFields } from "./statusTileState";

interface TileRecord {
    id: string;
    name: string;
    value?: Record<string, unknown> | null;
    [key: string]: unknown;
}

const POLL_INTERVAL_MS = 10000;

/**
 * `status_tile` widget — a single traffic-light-style status tile.
 *
 * Reads ONE field off ONE record (first match of `filter`, ordered by `order`)
 * and maps its value to a coloured indicator + label via `config.value_map`.
 * For CUSTOM_OBJECT records the field is read from the JSON `value`
 * (`value.<field>`); for other record types it is a top-level column.
 *
 * An optional `config.stale` block turns a missing heartbeat (the record's own
 * freshness timestamp + grace has passed) into a dead/no-heartbeat state, unless
 * the current value is one that legitimately sleeps indefinitely (`skip_when_values`).
 *
 * Per the affordance design principle this is INERT display (a status indicator,
 * not a button): no pointer cursor, no hover affordance, no click target.
 */
export const StatusTileWidget = ({ config }: WidgetComponentProps<StatusTileWidgetConfig>) => {
    const [record, setRecord] = useState<TileRecord | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState(true);
    const isMounted = useRef(true);
    // The last record committed to state — used to skip a no-op re-render when a
    // `live` re-poll returns the identical record.
    const prevRecordRef = useRef<TileRecord | null | undefined>(undefined);

    const recordType = config?.record_type;
    const apiType = typeof recordType === "string" ? recordType.toLowerCase() : "";
    const isCustomObject = apiType === "custom_object";
    const field = config?.field ?? "";
    const order = config?.order ?? null;
    const live = config?.live ?? false;
    const filter =
        config?.filter == null
            ? null
            : typeof config.filter === "string"
            ? config.filter
            : JSON.stringify(config.filter);

    // For CUSTOM_OBJECT the state field lives inside the JSON `value`; for other
    // record types it is a top-level column selected by name.
    const selection = isCustomObject ? "value" : field;

    const fetchRecord = useCallback(async (): Promise<boolean> => {
        if (!apiType) {
            if (isMounted.current) {
                setError("Widget config is missing a record_type.");
                setLoading(false);
            }
            return false;
        }
        if (!field) {
            if (isMounted.current) {
                setError("Widget config is missing a field.");
                setLoading(false);
            }
            return false;
        }
        const listField = `${apiType}_plain_list`;
        const query = `query DashboardStatusTile($order: String, $filter: String) {
            ${listField}(limit: 1, order: $order, filter: $filter) {
                id
                name
                ${selection}
            }
        }`;
        try {
            const response = await graphqlPost({ query, variables: { order, filter } });
            const list = response?.data?.[listField];
            const next: TileRecord | null =
                Array.isArray(list) && list.length > 0 ? list[0] : null;
            // Skip the re-render (and the lastUpdatedAt bump) when a `live`
            // re-poll returns the identical record.
            const changed = !isEqual(prevRecordRef.current, next);
            if (isMounted.current) {
                if (changed) {
                    prevRecordRef.current = next;
                    setRecord(next);
                }
                setError(null);
                setLoading(false);
            }
            return changed;
        } catch (err: any) {
            if (isMounted.current) {
                setError(describeWidgetFetchError(err, "Failed to load status."));
                setLoading(false);
            }
            return false;
        }
    }, [apiType, field, selection, order, filter]);

    const { lastUpdatedAt, refresh, isRefreshing } = useDashboardLiveRefresh({
        live,
        pollIntervalMs: POLL_INTERVAL_MS,
        fetch: fetchRecord,
    });

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        setLoading(true);
    }, [fetchRecord]);

    // The field set a status_tile reads from: a CO's JSON `value`, else the record.
    const fields: TileFields | null = record
        ? isCustomObject
            ? (record.value as TileFields | null) ?? null
            : (record as TileFields)
        : null;

    // Resolve state + appearance (value_map → default → staleness override) via the
    // pure, unit-tested helper.
    const { stateKey, entry } = resolveStatusTile(config, fields, Date.now());

    let content;
    // Keep the last record on a failed re-fetch; surface the error inline on the
    // live bar instead of blanking.
    if (error && !record) {
        content = <div className="DashboardWidget__error">{error}</div>;
    } else if (isLoading) {
        content = <div className="DashboardWidget__empty">Loading…</div>;
    } else if (!record) {
        content = <div className="DashboardWidget__empty">No matching record.</div>;
    } else if (!entry) {
        content = (
            <div className="DashboardStatusTile">
                <span className="DashboardStatusTile__indicator">
                    <StatusBadge type="dark-grey" round size="small" text="" />
                </span>
                <span className="DashboardStatusTile__label">{stateKey || "Unknown"}</span>
            </div>
        );
    } else {
        const detailText =
            entry.detail_field != null
                ? formatStatusTileDetail(
                      fields ? fields[entry.detail_field] : undefined,
                      entry.detail_field_format
                  )
                : entry.detail ?? "";
        content = (
            <div className="DashboardStatusTile">
                <span className="DashboardStatusTile__indicator">
                    <StatusBadge type={entry.color} round size="small" text="" />
                </span>
                <span className="DashboardStatusTile__label">{entry.label}</span>
                {detailText && <span className="DashboardStatusTile__detail">{detailText}</span>}
            </div>
        );
    }

    return (
        <DashboardLiveWidgetShell
            live={live}
            lastUpdatedAt={lastUpdatedAt}
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            error={error}
        >
            {content}
        </DashboardLiveWidgetShell>
    );
};
